/**
 * IndexedDB Cache Service for Qandil App
 * 
 * Caches data locally in the browser to reduce SQL reads from Cloudflare Durable Objects.
 * Works alongside WebSocket real-time updates to keep cache fresh.
 */

const DB_NAME = 'qandil-cache';
const DB_VERSION = 1;

// Store names matching the API data types
const STORES = {
  customers: 'customers',
  products: 'products',
  payments: 'payments',
  expenses: 'expenses',
  jobs: 'jobs',
  meta: 'meta', // For storing timestamps and metadata
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

class CacheService {
  private db: IDBDatabase | null = null;
  private dbReady: Promise<IDBDatabase> | null = null;

  constructor() {
    if (typeof window !== 'undefined' && window.indexedDB) {
      this.dbReady = this.openDB();
    }
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores with 'id' as keyPath
        if (!db.objectStoreNames.contains(STORES.customers)) {
          db.createObjectStore(STORES.customers, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.products)) {
          db.createObjectStore(STORES.products, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.payments)) {
          db.createObjectStore(STORES.payments, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORES.expenses)) {
          db.createObjectStore(STORES.expenses, { keyPath: 'id' });
        }
        // Jobs is a simple array, store with a fixed key
        if (!db.objectStoreNames.contains(STORES.jobs)) {
          db.createObjectStore(STORES.jobs, { keyPath: 'key' });
        }
        // Meta store for timestamps
        if (!db.objectStoreNames.contains(STORES.meta)) {
          db.createObjectStore(STORES.meta, { keyPath: 'key' });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve(this.db);
      };

      request.onerror = () => {
        console.warn('[Cache] Failed to open IndexedDB:', request.error);
        reject(request.error);
      };
    });
  }

  private async getDB(): Promise<IDBDatabase | null> {
    if (this.db) return this.db;
    if (!this.dbReady) return null;
    try {
      return await this.dbReady;
    } catch {
      return null;
    }
  }

  // ===== Generic CRUD Operations =====

  /**
   * Get all items from a store
   */
  async getAll<T>(storeName: StoreName): Promise<T[] | null> {
    const db = await this.getDB();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result as T[]);
        request.onerror = () => {
          console.warn(`[Cache] Failed to read from ${storeName}`);
          resolve(null);
        };
      } catch {
        resolve(null);
      }
    });
  }

  /**
   * Save all items to a store (replaces existing data)
   */
  async saveAll<T>(storeName: StoreName, items: T[]): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);

        // Clear existing data first
        store.clear();

        // Add all items
        for (const item of items) {
          store.put(item);
        }

        tx.oncomplete = () => {
          // Update the last-fetched timestamp
          this.setMeta(`${storeName}_lastFetch`, Date.now());
          resolve();
        };
        tx.onerror = () => {
          console.warn(`[Cache] Failed to write to ${storeName}`);
          resolve();
        };
      } catch {
        resolve();
      }
    });
  }

  /**
   * Add or update a single item in a store
   */
  async putItem<T>(storeName: StoreName, item: T): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        store.put(item);

        tx.oncomplete = () => resolve();
        tx.onerror = () => {
          console.warn(`[Cache] Failed to put item in ${storeName}`);
          resolve();
        };
      } catch {
        resolve();
      }
    });
  }

  /**
   * Delete a single item from a store by ID
   */
  async deleteItem(storeName: StoreName, id: string): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        store.delete(id);

        tx.oncomplete = () => resolve();
        tx.onerror = () => {
          console.warn(`[Cache] Failed to delete item from ${storeName}`);
          resolve();
        };
      } catch {
        resolve();
      }
    });
  }

  // ===== Jobs (special: stored as array with fixed key) =====

  async getJobs(): Promise<string[] | null> {
    const db = await this.getDB();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORES.jobs, 'readonly');
        const store = tx.objectStore(STORES.jobs);
        const request = store.get('jobs-list');

        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.data : null);
        };
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  async saveJobs(jobs: string[]): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORES.jobs, 'readwrite');
        const store = tx.objectStore(STORES.jobs);
        store.put({ key: 'jobs-list', data: jobs });

        tx.oncomplete = () => {
          this.setMeta('jobs_lastFetch', Date.now());
          resolve();
        };
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  // ===== Meta / Timestamps =====

  private async setMeta(key: string, value: unknown): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORES.meta, 'readwrite');
        const store = tx.objectStore(STORES.meta);
        store.put({ key, value });
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }

  async getMeta(key: string): Promise<unknown | null> {
    const db = await this.getDB();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORES.meta, 'readonly');
        const store = tx.objectStore(STORES.meta);
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.value : null);
        };
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  /**
   * Check if cache has data for a store
   */
  async hasCachedData(storeName: StoreName): Promise<boolean> {
    const lastFetch = await this.getMeta(`${storeName}_lastFetch`);
    return lastFetch !== null;
  }

  /**
   * Clear all cached data (e.g., on logout)
   */
  async clearAll(): Promise<void> {
    const db = await this.getDB();
    if (!db) return;

    const storeNames = Object.values(STORES);

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeNames, 'readwrite');
        for (const name of storeNames) {
          tx.objectStore(name).clear();
        }
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }
}

export const cacheService = new CacheService();
